import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Textarea,
} from '@agion/ui'
import { History, MessagesSquare, RefreshCw } from 'lucide-react'
import type { RolloutControlProps } from './rollout_catalog'
import { isRolloutOutcomeUncertain, isRolloutRevisionConflict, RolloutPageError, rolloutErrorText } from './rollout_refusal'
import { ManagedChatKeyHealthPanel } from './ManagedChatKeyHealthPanel'
import {
  changeManagedChatAddressPolicy,
  getManagedChatAddressPolicy,
  listManagedChatAddressHistory,
  type ManagedChatAddressHistoryEntry,
  type ManagedChatAddressLevel,
  type ManagedChatAddressLimit,
  type ManagedChatAddressPolicy,
} from './infra_managed_chat_rollout.service'

const levels: readonly ManagedChatAddressLevel[] = ['off', 'on']
const historyDateOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }
const prefix = 'rolloutControls.managedChat'

function levelControlID(level: ManagedChatAddressLevel): string {
  return `managed-chat-address-${level}`
}

interface LimitDraft {
  useDefault: boolean
  amount: string
}

function draftFrom(limit: ManagedChatAddressLimit): LimitDraft {
  return { useDefault: limit.own === null, amount: String(limit.own ?? limit.default) }
}

/**
 * The limit change a draft asks for: undefined keeps the organisation's
 * limit, null returns it to the default, a number sets it. The server
 * validates the number again and decides.
 */
function limitChange(limit: ManagedChatAddressLimit, draft: LimitDraft): { change?: number | null; invalid: boolean } {
  if (draft.useDefault) return limit.own === null ? { invalid: false } : { change: null, invalid: false }
  const text = draft.amount.trim()
  const amount = Number(text)
  if (!/^[1-9]\d*$/.test(text) || amount > limit.maximum) return { invalid: true }
  return amount === limit.own ? { invalid: false } : { change: amount, invalid: false }
}

/**
 * The Agion-managed chat address card (PLAN-20261015 slice 15,
 * agion/Agion#3473; ADR-003 G1, G7, G15). Staff turn on an organisation's
 * managed chat addresses and set the most it may have; the card also shows
 * the health of Agion's Cloudflare keys. Every read and write goes through
 * the staff route, also for the active organisation, so only a verified
 * platform administrator reaches it. The server validates each change and
 * audits it; this card shows what it answers.
 */
export function ManagedChatAddressCard({ organization }: RolloutControlProps) {
  const { t, i18n } = useTranslation('pages')
  const cardOrganizationRef = useRef(organization.id)
  const loadGeneration = useRef(0)
  const mutationGeneration = useRef(0)
  cardOrganizationRef.current = organization.id
  const [policy, setPolicy] = useState<ManagedChatAddressPolicy | null>(null)
  const [history, setHistory] = useState<ManagedChatAddressHistoryEntry[]>([])
  const [targetLevel, setTargetLevel] = useState<ManagedChatAddressLevel>('off')
  const [draft, setDraft] = useState<LimitDraft>({ useDefault: true, amount: '' })
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [historyWarning, setHistoryWarning] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const isCurrent = useCallback(
    (generation: number, counter: { current: number }, organizationId: string) =>
      generation === counter.current && cardOrganizationRef.current === organizationId,
    [],
  )

  const showPolicy = useCallback((current: ManagedChatAddressPolicy) => {
    setPolicy(current)
    setTargetLevel(current.level)
    setDraft(draftFrom(current.addressLimit))
    setReason('')
    setConfirmed(false)
  }, [])

  const refreshHistory = useCallback(async (generation: number, counter: { current: number }, organizationId: string) => {
    try {
      const currentHistory = await listManagedChatAddressHistory(organizationId)
      if (!isCurrent(generation, counter, organizationId)) return
      setHistory(currentHistory)
    } catch {
      if (!isCurrent(generation, counter, organizationId)) return
      setHistoryWarning(t(`${prefix}.historyRefreshError`))
    }
  }, [isCurrent, t])

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current
    const requestedOrganizationId = organization.id
    setIsLoading(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    setPolicy(null)
    setHistory([])
    try {
      const current = await getManagedChatAddressPolicy(requestedOrganizationId)
      if (!isCurrent(generation, loadGeneration, requestedOrganizationId)) return
      if (current.organizationId !== requestedOrganizationId) {
        throw new RolloutPageError(t('rolloutControls.target.organizationMismatch'))
      }
      showPolicy(current)
      await refreshHistory(generation, loadGeneration, requestedOrganizationId)
    } catch (loadError) {
      if (!isCurrent(generation, loadGeneration, requestedOrganizationId)) return
      setError(rolloutErrorText(loadError, t, 'rolloutControls.loadError'))
    } finally {
      if (isCurrent(generation, loadGeneration, requestedOrganizationId)) setIsLoading(false)
    }
  }, [isCurrent, organization.id, refreshHistory, showPolicy, t])

  const invalidatePendingRequests = useCallback(() => {
    loadGeneration.current++
    mutationGeneration.current++
  }, [])

  useEffect(() => {
    mutationGeneration.current++
    setIsSaving(false)
    void load()
    return invalidatePendingRequests
  }, [invalidatePendingRequests, load])

  const pending = useMemo(
    () => (policy ? limitChange(policy.addressLimit, draft) : { invalid: false }),
    [policy, draft],
  )
  const reasonBytes = useMemo(() => new TextEncoder().encode(reason.trim()).length, [reason])
  const hasChange = policy !== null && (targetLevel !== policy.level || pending.change !== undefined)
  const canApply = hasChange && !pending.invalid && reasonBytes > 0 && reasonBytes <= 500 && confirmed && !isSaving

  const apply = async () => {
    if (!policy || !canApply) return
    const generation = ++mutationGeneration.current
    const requestedOrganizationId = organization.id
    setIsSaving(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    try {
      const updated = await changeManagedChatAddressPolicy(requestedOrganizationId, {
        expectedRevision: policy.revision,
        level: targetLevel,
        ...(pending.change !== undefined ? { addressLimit: pending.change } : {}),
        reason: reason.trim(),
        confirmed: true,
      })
      if (!isCurrent(generation, mutationGeneration, requestedOrganizationId)) return
      if (updated.organizationId !== requestedOrganizationId) {
        throw new RolloutPageError(t('rolloutControls.target.organizationMismatch'))
      }
      showPolicy(updated)
      setSaved(true)
      await refreshHistory(generation, mutationGeneration, requestedOrganizationId)
    } catch (saveError) {
      if (!isCurrent(generation, mutationGeneration, requestedOrganizationId)) return
      if (isRolloutRevisionConflict(saveError) || isRolloutOutcomeUncertain(saveError)) {
        await load()
        if (isCurrent(generation, mutationGeneration, requestedOrganizationId)) {
          setError(isRolloutRevisionConflict(saveError)
            ? t(`${prefix}.conflict`)
            : rolloutErrorText(saveError, t, `${prefix}.saveError`))
        }
      } else {
        setError(rolloutErrorText(saveError, t, `${prefix}.saveError`))
      }
    } finally {
      if (isCurrent(generation, mutationGeneration, requestedOrganizationId)) setIsSaving(false)
    }
  }

  const levelLabel = (level: ManagedChatAddressLevel) => t(`${prefix}.levels.${level}.label`)
  const limitText = (amount: number | null | undefined) => amount === null || amount === undefined
    ? t(`${prefix}.limitDefault`, { amount: policy?.addressLimit.default ?? '' })
    : String(amount)
  // A history row carries no default of its time, so it names the default without an amount.
  const historyLimitText = (amount: number | undefined) => amount === undefined ? t(`${prefix}.historyDefault`) : String(amount)
  const draftLimitText = !policy ? '' : pending.change === undefined
    ? limitText(policy.addressLimit.own)
    : limitText(pending.change)

  return (
    <Card>
      <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-sky-50 p-2 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
              <MessagesSquare className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <CardTitle>{t(`${prefix}.title`)}</CardTitle>
              <CardDescription className="mt-1">{t(`${prefix}.description`)}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="purple">{t(`${prefix}.organizationScoped`)}</Badge>
            {policy && <Badge variant={policy.level === 'on' ? 'success' : 'default'}>{levelLabel(policy.level)}</Badge>}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : error && !policy ? (
          <Alert variant="error">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{error}</span>
              <Button variant="secondary" size="sm" onClick={() => void load()}>
                <RefreshCw className="mr-1 h-4 w-4" />{t('rolloutControls.refresh')}
              </Button>
            </div>
          </Alert>
        ) : policy ? (
          <>
            {error && <Alert variant="error">{error}</Alert>}
            {saved && <Alert variant="success">{t(`${prefix}.saved`)}</Alert>}
            {historyWarning && <Alert variant="warning">{historyWarning}</Alert>}
            <Alert variant="info">{t(`${prefix}.existingNotice`)}</Alert>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)]">
              <div>
                <div className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-200">{t(`${prefix}.targetLevel`)}</div>
                <RadioGroup
                  value={targetLevel}
                  onValueChange={(value) => {
                    setTargetLevel(value as ManagedChatAddressLevel)
                    setConfirmed(false)
                    setSaved(false)
                  }}
                  className="space-y-2"
                >
                  {levels.map((level) => {
                    const id = levelControlID(level)
                    return (
                      <div
                        key={level}
                        className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${targetLevel === level
                          ? 'border-sky-400 bg-sky-50/70 dark:border-sky-600 dark:bg-sky-950/20'
                          : 'border-slate-200 dark:border-slate-700'}`}
                      >
                        <RadioGroupItem id={id} value={level} aria-label={levelLabel(level)} className="mt-0.5" />
                        <Label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                          <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">{levelLabel(level)}</span>
                          <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                            {t(`${prefix}.levels.${level}.description`)}
                          </span>
                        </Label>
                      </div>
                    )
                  })}
                </RadioGroup>
              </div>

              <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.currentOrganization`)}</dt>
                    <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">{organization.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.currentLevel`)}</dt>
                    <dd className="mt-1">{levelLabel(policy.level)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.currentLimit`)}</dt>
                    <dd className="mt-1" data-testid="managed-chat-current-limit">
                      {t(policy.addressLimit.own === null ? `${prefix}.effectiveDefault` : `${prefix}.effectiveOwn`, {
                        amount: policy.addressLimit.effective,
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.revision`)}</dt>
                    <dd className="mt-1 font-mono">{policy.revision}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <fieldset className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <legend className="px-1 text-sm font-medium text-slate-900 dark:text-slate-100">{t(`${prefix}.limitTitle`)}</legend>
              <p className="mb-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                {t(`${prefix}.limitNotice`, { default: policy.addressLimit.default, maximum: policy.addressLimit.maximum })}
              </p>
              <Checkbox
                id="managed-chat-limit-default"
                checked={draft.useDefault}
                disabled={isSaving}
                onCheckedChange={(checked) => {
                  setDraft((current) => ({ ...current, useDefault: checked === true }))
                  setConfirmed(false)
                  setSaved(false)
                }}
                label={t(`${prefix}.useDefault`, { amount: policy.addressLimit.default })}
              />
              {!draft.useDefault && (
                <div className="mt-3 sm:max-w-xs">
                  <Input
                    id="managed-chat-limit-amount"
                    label={t(`${prefix}.limitLabel`)}
                    inputMode="numeric"
                    value={draft.amount}
                    disabled={isSaving}
                    onChange={(event) => {
                      setDraft((current) => ({ ...current, amount: event.target.value }))
                      setConfirmed(false)
                      setSaved(false)
                    }}
                    error={pending.invalid ? t(`${prefix}.limitInvalid`, { maximum: policy.addressLimit.maximum }) : undefined}
                  />
                </div>
              )}
            </fieldset>

            <div>
              <Textarea
                id="managed-chat-address-reason"
                label={t(`${prefix}.reasonLabel`)}
                placeholder={t(`${prefix}.reasonPlaceholder`)}
                value={reason}
                rows={3}
                onChange={(event) => {
                  setReason(event.target.value.replace(/[\r\n\t]+/g, ' '))
                  setSaved(false)
                }}
                error={reasonBytes > 500 ? t(`${prefix}.reasonTooLong`) : undefined}
              />
              <div className="mt-1 text-right text-xs text-slate-400">{t(`${prefix}.reasonBytes`, { bytes: reasonBytes })}</div>
            </div>

            {hasChange && (
              <Checkbox
                id="managed-chat-address-confirmation"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                label={t(`${prefix}.confirmation`, {
                  organization: organization.name,
                  level: levelLabel(targetLevel),
                  limit: draftLimitText,
                })}
              />
            )}

            <ManagedChatKeyHealthPanel />

            <div>
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                <History className="h-4 w-4" aria-hidden="true" />
                {t(`${prefix}.historyTitle`)}
              </div>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{t(`${prefix}.noHistory`)}</p>
              ) : (
                <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
                  {history.map((entry) => (
                    <li key={entry.revision} className="p-3 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium text-slate-800 dark:text-slate-200">
                          {t(`${prefix}.historyTransition`, { from: levelLabel(entry.previousLevel), to: levelLabel(entry.level) })}
                        </span>
                        <time className="text-xs text-slate-400" dateTime={entry.changedAt}>
                          {new Intl.DateTimeFormat(i18n.language, historyDateOptions).format(new Date(entry.changedAt))}
                        </time>
                      </div>
                      {entry.previousAddressLimit !== entry.addressLimit && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {t(`${prefix}.historyLimit`, { from: historyLimitText(entry.previousAddressLimit), to: historyLimitText(entry.addressLimit) })}
                        </p>
                      )}
                      <p className="mt-1 text-slate-600 dark:text-slate-400">{entry.reason}</p>
                      <div className="mt-1 font-mono text-[11px] text-slate-400">
                        {t(`${prefix}.historyEvidence`, { actor: entry.actorId, revision: entry.revision })}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        ) : null}
      </CardContent>

      {policy && !isLoading && (
        <CardFooter className="justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
          <Button variant="primary" disabled={!canApply} isLoading={isSaving} onClick={() => void apply()}>
            {t(`${prefix}.apply`)}
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}
